import type { CitationParsedSegment } from "../types";

const ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
};

function decodeEntitiesOnce(input: string): string {
  return input.replace(/&(amp|lt|gt|quot|#39|apos);/g, (entity) => ENTITIES[entity] ?? entity);
}

const CITATION_TAG = /<u\b([^>]*)>([\s\S]*?)<\/u>/gi;
const ATTR = /([a-zA-Z_:-][a-zA-Z0-9_:.-]*)\s*=\s*("([^"]*)"|'([^']*)')/g;

function parseAttrs(raw: string): { id?: string; dataSource?: string; dataType?: string } {
  const out: { id?: string; dataSource?: string; dataType?: string } = {};
  let match: RegExpExecArray | null;
  ATTR.lastIndex = 0;
  while ((match = ATTR.exec(raw)) !== null) {
    const name = match[1].toLowerCase();
    const value = match[3] ?? match[4] ?? "";
    if (name === "id") out.id = value;
    else if (name === "data-datasource" || name === "datasource") out.dataSource = value;
    else if (name === "data-type" || name === "type") out.dataType = value;
  }
  return out;
}

export function parseUnderlineTags(input: string): CitationParsedSegment[] {
  if (!input) return [];

  const looksEscaped = /&(?:lt|amp|quot|#39|apos);/.test(input);
  const text = looksEscaped ? decodeEntitiesOnce(input) : input;

  const segments: CitationParsedSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  CITATION_TAG.lastIndex = 0;

  while ((match = CITATION_TAG.exec(text)) !== null) {
    const [whole, rawAttrs, inner] = match;
    const attrs = parseAttrs(rawAttrs);

    if (!attrs.id && !attrs.dataSource && !attrs.dataType) continue;

    if (match.index > lastIndex) {
      segments.push({ type: "text", content: text.slice(lastIndex, match.index) });
    }

    segments.push({
      type: "citation",
      content: inner,
      attributes: {
        id: attrs.id ?? "",
        dataSource: attrs.dataSource,
        dataType: attrs.dataType,
      },
    });

    lastIndex = match.index + whole.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", content: text.slice(lastIndex) });
  }

  if (segments.length === 0) {
    return [{ type: "text", content: input }];
  }
  return segments;
}

export function stripUnderlineTags(input: string): {
  cleanText: string;
  refs: Array<{
    id: string;
    dataSource?: string;
    dataType?: string;
    value: string;
    start: number;
    end: number;
  }>;
} {
  const segments = parseUnderlineTags(input);
  let cleanText = "";
  const refs: Array<{
    id: string;
    dataSource?: string;
    dataType?: string;
    value: string;
    start: number;
    end: number;
  }> = [];

  for (const segment of segments) {
    if (segment.type === "text") {
      cleanText += segment.content;
    } else {
      const start = cleanText.length;
      cleanText += segment.content;
      const end = cleanText.length;
      if (segment.attributes.id) {
        refs.push({
          id: segment.attributes.id,
          dataSource: segment.attributes.dataSource,
          dataType: segment.attributes.dataType,
          value: segment.content,
          start,
          end,
        });
      }
    }
  }

  return { cleanText, refs };
}
